'use client'

import {useParams} from 'next/navigation'
import {HTMLAttributes, useEffect, useRef} from 'react'
import {Button, SlideshowNative} from '@local/ui/components'
import {InternalLinkWithLabelType} from '@local/types'
import {cx} from '@local/utils/cx'

export interface SideNavProps extends HTMLAttributes<HTMLElement> {
	links: InternalLinkWithLabelType[]
	className?: string
}

export const SideNav = ({
	links,
	className,
}: SideNavProps) => {
	const params = useParams()
	const slug = params.slug

	const slideshowRef = useRef(null)

	const stripSlug = (slug) => {
		slug = slug.replace(/-/g, ' ')
		return slug
	}

	useEffect(() => {
		if (slideshowRef.current) {
			const activeLink = links.findIndex((link) => link.slug === slug)
			if (activeLink !== -1) {
				slideshowRef.current.scrollToIndex(activeLink)
			}
		}
	}, [slug, slideshowRef.current])

	return (
		<>
			{/* Desktop Version */}
			<div className={cx('lg-max:hidden flex flex-col py-30', className)}>
				<ul className='flex flex-col gap-y-10'>
					{links.map((link) => (
						<li key={`item-${link.slug}`}>
							<Button
								linkInput={link}
								variant='underlined'
								active={slug === link.slug}
								className={cx(
									'text-14 leading-none tracking-tight capitalize font-normal',
									slug === link.slug ? 'after:!scale-x-100' : 'text-grey-3 hover:text-grey-5',
								)}>
								{stripSlug(link.slug)}
							</Button>
						</li>
					))}
				</ul>
			</div>

			{/* Mobile Version */}
			{!!links.length && (
				<SlideshowNative
					ref={slideshowRef}
					withPrevNextNavigation
					disableObserverOnMobile
					prevNextButtonVariant='none'
					containerClassName='lg:hidden relative border-y border-grey-1 w-full'
					className={cx(
						'margin-px-1 py-24',
						'gap-20 lg:gutter-gap-1 scroll-pl-[var(--margin)] relative whitespace-nowrap',
					)}
					prevNextNavigationClassName={cx(
						'absolute inset-0 flex justify-between mt-0',
						'md:hidden',
					)}
					prevNextButtonClassName={cx(
						'bg-white-off margin-px-1 h-full',
						'disabled:opacity-0',
					)}>
					{links.map((link) => (
						<Button
							key={`button-${link.slug}`}
							variant='underlined'
							linkInput={link}
							className={cx(
								'text-14 tracking-tight capitalize !font-normal ',
								slug !== link.slug ? 'opacity-50' : 'after:!scale-x-100',
							)}>
							<span>{stripSlug(link.slug)}</span>
						</Button>
					))}
				</SlideshowNative>
			)}
		</>
	)
}
